import { FC, SVGProps, useCallback, useEffect, useState } from 'react';
import { PolicyTemplate, PolicyTemplateStaticCategory } from '../../../../../types/policies';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
    useDeletePolicyTemplate,
    UseDeleteTemplateParams,
    useDuplicatePolicyTemplate,
    UseDuplicatePolicyTemplateParams,
    useEnablePolicyTemplate,
    UseEnablePolicyTemplateParams,
    useUpdatePolicyTemplateFavorite,
    UseUpdatePolicyTemplateFavoriteParams
} from '../../../api';
import { isEmpty } from 'lodash';
import FavoritesEmptyState from '../FavoritesEmptyState';
import { useSnackAlert } from '../../../hooks/useSnackAlert';
import { Icons, SnackAlert, SnackAlertContent } from '@silverfort/sf-components';
import { getPolicyFieldsFromTemplate } from '../../utils/templates';
import DuplicateTemplateModal from '../DuplicateTemplateModal';
import DeleteTemplateModal from '../DeleteTemplateModal';
import { useDeleteTemplateModal, useDuplicateTemplateModal } from './hooks';
import CustomTemplateEmptyState from '../CustomTemplateEmptyState';
import { usePolicyTemplatesContext } from '../../context/PolicyTemplatesContext';
import { useHandleStartPolicyFromTemplate } from '../../hooks/useHandleStartPolicyFromTemplate';
import TemplateListItem from './TemplateListItem';
import { PolicyLockedFeatureUtils } from '../../../../../utils/policies';
import { usePricingModulesState } from '../../../../../context/contexts/PricingModuleContext';
import { usePolicyConfigurationContext } from '../../context/PolicyConfigurationContext';
import { PolicyTemplateCategory } from '../../types/category';

interface Props {
    categoryId: string;
    templates: PolicyTemplate[];
}

const CATEGORY_TO_EMPTY_STATE = {
    [PolicyTemplateStaticCategory.FAVORITES]: FavoritesEmptyState,
    [PolicyTemplateStaticCategory.CUSTOM]: CustomTemplateEmptyState
};

const SNACKBAR_TEXT_CLASSNAME = 'leading-[15.51px] h-4';
const SNACKBAR_BOLD_TEXT_CLASSNAME = 'leading-[15.87px] font-medium';

const getCategoryName = (categoryId: string, categories: PolicyTemplateCategory[]) =>
    categories.find((category) => category.id === categoryId)?.name ?? '';

const TemplatesList: FC<Props> = ({ categoryId, templates }) => {
    const { categories } = usePolicyTemplatesContext();
    const title = getCategoryName(categoryId, categories);
    const history = useHistory();
    const match = useRouteMatch();
    const { navigateToCreatePolicyFromTemplate } = useHandleStartPolicyFromTemplate();
    const { getTemplate, onEditTemplate, onAddTemplate, onRemoveTemplate } = usePolicyTemplatesContext();
    const { policyConfiguration = { hiddenRiskIndicators: [] } } = usePolicyConfigurationContext();
    const { pricingModulesState } = usePricingModulesState();

    const [templateForFavorite, setTemplateForFavorite] = useState<PolicyTemplate>();
    const [shouldTemplateFavorite, setShouldTemplateFavorite] = useState<boolean>();
    const { isOpen, snackAlertContent, popSnackAlert, dismissSnackAlert } = useSnackAlert();
    const {
        open: showDuplicateModal,
        templateToDuplicate,
        openDuplicateTemplateModal,
        closeDuplicateTemplateModal
    } = useDuplicateTemplateModal();
    const {
        open: showDeleteModal,
        templateToDelete,
        openDeleteTemplateModal,
        closeDeleteTemplateModal
    } = useDeleteTemplateModal();

    const onUpdateTemplateSuccess: UseUpdatePolicyTemplateFavoriteParams['onSuccess'] = useCallback(
        ({ id, ...templateDetails }) => {
            onEditTemplate(id, templateDetails);
        },
        [onEditTemplate]
    );

    const {
        updatePolicyTemplateFavorite,
        loading: updateFavoriteLoading,
        error: updateFavoriteError
    } = useUpdatePolicyTemplateFavorite({
        onSuccess: onUpdateTemplateSuccess
    });

    const handleEnableTemplateSuccess: UseEnablePolicyTemplateParams['onSuccess'] = useCallback(
        (template: PolicyTemplate) => {
            onAddTemplate(template);
        },
        [onAddTemplate]
    );

    const { enablePolicyTemplate } = useEnablePolicyTemplate({ onSuccess: handleEnableTemplateSuccess });

    const handleDeleteTemplateSuccess: UseDeleteTemplateParams['onSuccess'] = useCallback(
        ({ templateId }) => {
            const deletedTemplate = getTemplate(templateId);

            closeDeleteTemplateModal();
            popSnackAlert({
                iconLeft: Icons.CircleCancel,
                message: (
                    <span className={SNACKBAR_TEXT_CLASSNAME}>
                        <span className={SNACKBAR_BOLD_TEXT_CLASSNAME}>{deletedTemplate?.name}</span> template deleted
                    </span>
                ),
                actionMessage: 'Undo',
                onAction: () => {
                    if (deletedTemplate) {
                        void enablePolicyTemplate(templateId);
                    }
                }
            });
            onRemoveTemplate(templateId);
        },
        [closeDeleteTemplateModal, popSnackAlert, onRemoveTemplate, getTemplate, enablePolicyTemplate]
    );

    const handleDeleteTemplateError: UseDeleteTemplateParams['onError'] = useCallback(
        ({ templateId }) => {
            const deletedTemplate = getTemplate(templateId);

            closeDeleteTemplateModal();
            popSnackAlert({
                iconLeft: Icons.CircleCancel,
                message: (
                    <span className={SNACKBAR_TEXT_CLASSNAME}>
                        Failed to delete <span className={SNACKBAR_BOLD_TEXT_CLASSNAME}>{deletedTemplate?.name}</span>
                        template
                    </span>
                )
            });
        },
        [closeDeleteTemplateModal, popSnackAlert, getTemplate]
    );

    const { deletePolicyTemplate } = useDeletePolicyTemplate({
        onSuccess: handleDeleteTemplateSuccess,
        onError: handleDeleteTemplateError
    });

    const handleDuplicateTemplateSuccess: UseDuplicatePolicyTemplateParams['onSuccess'] = useCallback(
        ({ originalId, duplicatedId, name }) => {
            const duplicatedTemplate = getTemplate(originalId);

            closeDuplicateTemplateModal();

            if (duplicatedTemplate) {
                popSnackAlert({
                    iconLeft: Icons.CircleCheck,
                    message: (
                        <span className={SNACKBAR_TEXT_CLASSNAME}>
                            <span className={SNACKBAR_BOLD_TEXT_CLASSNAME}>{duplicatedTemplate.name}</span> Duplicated
                            successfully
                        </span>
                    ),
                    actionMessage: 'Undo',
                    onAction: () => {
                        void deletePolicyTemplate(originalId);
                    }
                });

                duplicatedTemplate &&
                    onAddTemplate({ ...duplicatedTemplate, id: duplicatedId, name, policyName: name });
            }
        },
        [getTemplate, closeDuplicateTemplateModal, popSnackAlert, onAddTemplate, deletePolicyTemplate]
    );

    const handleDuplicateTemplateError: UseDuplicatePolicyTemplateParams['onError'] = useCallback(
        ({ templateId }) => {
            const template = getTemplate(templateId);

            closeDuplicateTemplateModal();
            popSnackAlert({
                iconLeft: Icons.CircleCancel,
                message: (
                    <span className={SNACKBAR_TEXT_CLASSNAME}>
                        Failed to duplicate <span className={SNACKBAR_BOLD_TEXT_CLASSNAME}>{template?.name}</span>{' '}
                        template
                    </span>
                )
            });
        },
        [closeDuplicateTemplateModal, getTemplate, popSnackAlert]
    );

    const { duplicatePolicyTemplate } = useDuplicatePolicyTemplate({
        onSuccess: handleDuplicateTemplateSuccess,
        onError: handleDuplicateTemplateError
    });

    const updateTemplateFavorite = useCallback(
        async (template: PolicyTemplate, isFavorite: boolean) => {
            setTemplateForFavorite(template);
            setShouldTemplateFavorite(isFavorite);
            await updatePolicyTemplateFavorite(template.id, isFavorite);
        },
        [updatePolicyTemplateFavorite]
    );

    const addTemplateToFavorites = useCallback(
        async (template: PolicyTemplate) => {
            await updateTemplateFavorite(template, true);
        },
        [updateTemplateFavorite]
    );

    const removeTemplateFromFavorites = useCallback(
        async (template: PolicyTemplate) => {
            await updateTemplateFavorite(template, false);
        },
        [updateTemplateFavorite]
    );

    useEffect(() => {
        if (templateForFavorite && shouldTemplateFavorite !== undefined && !updateFavoriteLoading) {
            if (shouldTemplateFavorite) {
                popSnackAlert({
                    message: (
                        <span className={SNACKBAR_TEXT_CLASSNAME}>
                            <span className={SNACKBAR_BOLD_TEXT_CLASSNAME}>{templateForFavorite.name}</span> added to
                            favorites
                        </span>
                    )
                });
            } else {
                popSnackAlert({
                    iconLeft: Icons.CircleCancel as FC<SVGProps<SVGSVGElement>>,
                    message: (
                        <span className={SNACKBAR_TEXT_CLASSNAME}>
                            <span className={SNACKBAR_BOLD_TEXT_CLASSNAME}>{templateForFavorite.name}</span> template
                            removed from favorites
                        </span>
                    ),
                    actionMessage: 'Undo',
                    onAction: () => addTemplateToFavorites(templateForFavorite)
                });
            }
        }
    }, [templateForFavorite, shouldTemplateFavorite, updateFavoriteLoading, popSnackAlert, addTemplateToFavorites]);

    useEffect(() => {
        if (templateForFavorite && shouldTemplateFavorite !== undefined && !!updateFavoriteError) {
            if (shouldTemplateFavorite) {
                popSnackAlert({
                    iconLeft: Icons.CircleCancel as FC<SVGProps<SVGSVGElement>>,
                    message: (
                        <span className={SNACKBAR_TEXT_CLASSNAME}>
                            Failed to add{' '}
                            <span className={SNACKBAR_BOLD_TEXT_CLASSNAME}>{templateForFavorite.name}</span> to
                            favorites
                        </span>
                    ),
                    actionMessage: 'Retry',
                    onAction: () => addTemplateToFavorites(templateForFavorite)
                });
            } else {
                popSnackAlert({
                    iconLeft: Icons.CircleCancel as FC<SVGProps<SVGSVGElement>>,
                    message: (
                        <span className={SNACKBAR_TEXT_CLASSNAME}>
                            Failed to remove{' '}
                            <span className={SNACKBAR_BOLD_TEXT_CLASSNAME}>{templateForFavorite.name}</span> to
                            favorites
                        </span>
                    ),
                    actionMessage: 'Retry',
                    onAction: () => removeTemplateFromFavorites(templateForFavorite)
                });
            }
        }
    }, [
        templateForFavorite,
        shouldTemplateFavorite,
        updateFavoriteLoading,
        popSnackAlert,
        updateFavoriteError,
        addTemplateToFavorites,
        removeTemplateFromFavorites
    ]);

    const startFromNonCustomTemplate = (template: PolicyTemplate) => {
        history.push(`${match.url}/Create/${template.id}`);
    };

    const startFromCustomTemplate = (template: PolicyTemplate) => {
        navigateToCreatePolicyFromTemplate(template.id, getPolicyFieldsFromTemplate(template));
    };

    const handleStartFromTemplateClick = (template: PolicyTemplate) => {
        if (template.typeId === PolicyTemplateStaticCategory.CUSTOM) {
            startFromCustomTemplate(template);
        } else {
            startFromNonCustomTemplate(template);
        }
    };

    const handleMenuEditClick = (template: PolicyTemplate) => {
        history.push(`/Policy?templateToEdit=${template.id}`, {
            policy: getPolicyFieldsFromTemplate(template)
        });
    };

    const handleMenuDeleteClick = (template: PolicyTemplate) => {
        openDeleteTemplateModal(template);
    };

    const handleMenuDuplicateClick = (template: PolicyTemplate) => {
        openDuplicateTemplateModal(template);
    };

    const duplicateTemplate = (templateId: PolicyTemplate['id'], templateName: string) => {
        void duplicatePolicyTemplate(templateId, templateName);
        closeDuplicateTemplateModal();
    };

    const deleteTemplate = (templateId: PolicyTemplate['id']) => {
        void deletePolicyTemplate(templateId);
        closeDeleteTemplateModal();
    };

    const getContent = () => {
        if (isEmpty(templates)) {
            const emptyState = CATEGORY_TO_EMPTY_STATE[categoryId];
            return (
                <div className="rounded-[8px] w-full border border-sf-gray-100 px-8 flex flex-col items-center">
                    {emptyState?.({})}
                </div>
            );
        }

        return (
            <>
                <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-x-6 gap-y-4">
                    {templates.map((template) => (
                        <TemplateListItem
                            template={template}
                            addTemplateToFavorites={addTemplateToFavorites}
                            removeTemplateFromFavorites={removeTemplateFromFavorites}
                            handleStartFromTemplateClick={handleStartFromTemplateClick}
                            handleMenuEditClick={handleMenuEditClick}
                            handleMenuDeleteClick={handleMenuDeleteClick}
                            handleMenuDuplicateClick={handleMenuDuplicateClick}
                            startFromTemplateLockedFeatureConfig={PolicyLockedFeatureUtils.getPolicyTemplatesLockedFeatureTooltipConfig(
                                {
                                    template,
                                    pricingModulesState,
                                    hiddenRiskIndicators: policyConfiguration.hiddenRiskIndicators
                                }
                            )}
                        />
                    ))}
                </div>
            </>
        );
    };

    return (
        <>
            <div className="h-full flex flex-col relative">
                <div className="pt-10 pb-6 px-6 overflow-y-auto flex flex-col gap-4">
                    <span className="h-[17px] font-sf text-14 font-medium leading-[17.09px] text-sf-gray-500">
                        {title} ({templates.length})
                    </span>
                    {getContent()}
                </div>

                <SnackAlert
                    open={isOpen}
                    autoHideDuration={2000}
                    onClose={dismissSnackAlert}
                    className="absolute"
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                    }}>
                    <SnackAlertContent {...snackAlertContent} />
                </SnackAlert>
            </div>

            <DuplicateTemplateModal
                open={showDuplicateModal}
                template={templateToDuplicate}
                onClose={closeDuplicateTemplateModal}
                onDuplicateClick={duplicateTemplate}
            />

            <DeleteTemplateModal
                open={showDeleteModal}
                template={templateToDelete}
                onClose={closeDeleteTemplateModal}
                onDeleteClick={deleteTemplate}
            />
        </>
    );
};

export default TemplatesList;
